/**
 * NN aero coefficients (FXC-9009) — integrate the flowfield-NN's surface pressure (Cp) over each
 * foil element to get pressure CL/CD. This turns the field surrogate into a CHEAP AERO SURROGATE the
 * High-Lift workbench's multifidelity composite can use as a PRIOR: the NN was trained on RANS, so
 * NN_CL ≈ RANS_CL inside the envelope and the residual-GP only has to model the NN's extrapolation
 * error (a much smaller residual than the analytic flexfoil prior leaves). See nnPrior.ts for the
 * PriorResponse wrapper that drops this into `mergeResidual`.
 *
 * The integration is a pure function of (contours, alpha, Cp-sampler) so it unit-tests against a
 * synthetic Cp field — no trained weights needed (nnAero.test.ts). `nnSurfaceForces` wires the real
 * NN forward in as the sampler.
 */
import { type NN, type NNCond, forward, buildRaw } from "./nnInfer";

export type { NNCond };

export interface FoilElement {
  name: string;
  contour: number[][];
}

export interface NNForces {
  /** Pressure lift coefficient (freestream-resolved). NN is trained on RANS Cp, so ≈ RANS CL. */
  CL: number;
  /** Pressure drag coefficient (freestream-resolved). Excludes skin friction → a prior, not truth. */
  CD: number;
  /** Body-axis pressure-force coefficients (x downstream, z up). */
  CFx: number;
  CFz: number;
  perElement: { name: string; CFx: number; CFz: number }[];
}

/** Sample Cp at a batch of (x,z) points for one flight condition. */
export type CpSampler = (pts: number[][]) => number[];

/** Signed polygon area (shoelace); > 0 ⟺ counter-clockwise winding. */
function signedArea(c: number[][]): number {
  let a = 0;
  for (let i = 0, n = c.length; i < n; i++) {
    const p = c[i]!;
    const q = c[(i + 1) % n]!;
    a += p[0]! * q[1]! - q[0]! * p[1]!;
  }
  return a / 2;
}

/**
 * Pressure forces from a Cp field over closed element contours. Pressure acts inward, so the
 * coefficient force on a segment is −Cp·n̂·ds (n̂ = OUTWARD unit normal, ds in chord units). Winding
 * is detected per element so the outward normal is correct for either orientation. Lift/drag are the
 * body-axis force resolved into the freestream frame at angle `alpha` (degrees):
 *   CL = −CFx·sin α + CFz·cos α,   CD = CFx·cos α + CFz·sin α.
 */
export function surfaceForcesFromCp(
  elements: FoilElement[],
  alpha: number,
  sampleCp: CpSampler,
): NNForces {
  const mids: number[][] = [];
  const seg: { el: number; nx: number; nz: number; ds: number }[] = [];
  elements.forEach((el, ei) => {
    const c = el.contour;
    if (!c || c.length < 3) return;
    const ccw = signedArea(c) > 0;
    for (let i = 0; i < c.length; i++) {
      const p = c[i]!;
      const q = c[(i + 1) % c.length]!;
      const dx = q[0]! - p[0]!;
      const dz = q[1]! - p[1]!;
      const ds = Math.hypot(dx, dz);
      if (ds < 1e-12) continue;
      // right-of-travel normal (dz, −dx) is OUTWARD for CCW winding; flip for CW.
      let nx = dz / ds;
      let nz = -dx / ds;
      if (!ccw) {
        nx = -nx;
        nz = -nz;
      }
      mids.push([(p[0]! + q[0]!) / 2, (p[1]! + q[1]!) / 2]);
      seg.push({ el: ei, nx, nz, ds });
    }
  });

  const cp = sampleCp(mids);
  let CFx = 0;
  let CFz = 0;
  const per = elements.map((el) => ({ name: el.name, CFx: 0, CFz: 0 }));
  for (let i = 0; i < seg.length; i++) {
    const s = seg[i]!;
    const f = -(cp[i] ?? 0) * s.ds; // magnitude along the outward normal (−Cp·ds)
    const fx = f * s.nx;
    const fz = f * s.nz;
    CFx += fx;
    CFz += fz;
    const pe = per[s.el]!;
    pe.CFx += fx;
    pe.CFz += fz;
  }

  const a = (alpha * Math.PI) / 180;
  const CL = -CFx * Math.sin(a) + CFz * Math.cos(a);
  const CD = CFx * Math.cos(a) + CFz * Math.sin(a);
  return { CL, CD, CFx, CFz, perElement: per };
}

/**
 * Integrate the trained NN's surface Cp over the foil → pressure CL/CD. The NN reads each surface
 * point's signed distance to all three elements (the body-fitted coordinate it was trained on), so
 * the Cp it returns on the boundary is the model's surface pressure.
 */
export function nnSurfaceForces(nn: NN, cond: NNCond, elements: FoilElement[]): NNForces {
  const iCp = nn.channels.indexOf("Cp");
  const NC = nn.channels.length;
  const sampleCp: CpSampler = (pts) => {
    const N = pts.length;
    if (N === 0) return [];
    const px = new Float32Array(N);
    const pz = new Float32Array(N);
    for (let k = 0; k < N; k++) {
      px[k] = pts[k]![0]!;
      pz[k] = pts[k]![1]!;
    }
    const raw = buildRaw(nn, cond, elements, px, pz, N);
    const pred = forward(nn, raw, N);
    const out = new Array<number>(N);
    for (let k = 0; k < N; k++) out[k] = pred[k * NC + iCp]!;
    return out;
  };
  return surfaceForcesFromCp(elements, cond.alpha, sampleCp);
}
